import { apiJobRoles } from "@/api/job-role.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiJobRoleFilter, ApiJobRoleSavePayload } from "@/types/job-role.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

export const jobRolesKeys = createQueryKeys("jobRoles", {
  search: (filter: ApiJobRoleFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiJobRoles.search(filter, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  searchInfinite: (filter: ApiJobRoleFilter) => ({ queryKey: [filter] }),
  byId: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiJobRoles.getById(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  byIds: (ids: number[]) => ({
    queryKey: [ids],
    queryFn: async ({ signal }) => {
      const result = await apiJobRoles.getByIds(ids, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useJobRolesSearch = (filter: ApiJobRoleFilter, enabled: boolean = true) => {
  const query = useQuery({
    ...jobRolesKeys.search(filter),
    enabled,
  });
  const options = useMemo(
    () => query.data?.items.map((role) => ({ id: role.id, title: role.title })) ?? [],
    [query.data]
  );

  return { ...query, options };
};

export const useJobRolesByIds = (ids: number[], enabled: boolean = true) => {
  const query = useQuery({
    ...jobRolesKeys.byIds(ids),
    enabled: enabled && ids.length > 0,
    staleTime: 60 * 1000,
  });

  return {
    ...query,
    options: query.data?.map((role) => ({ id: role.id, title: role.title })) ?? [],
  };
};

export const useJobRolesSearchInline = (selectedIds: number[] = [], enabled: boolean = true) => {
  const [filter, setFilter] = useState<ApiJobRoleFilter>({ page_number: 1 });
  const searchQuery = useJobRolesSearch(filter, enabled);
  const selectedQuery = useJobRolesByIds(selectedIds, enabled && selectedIds.length > 0);
  const options = useMemo(
    () =>
      [...selectedQuery.options, ...searchQuery.options].filter(
        (option, index, items) => items.findIndex((item) => item.id === option.id) === index
      ),
    [searchQuery.options, selectedQuery.options]
  );

  function search(text: string | undefined) {
    setFilter({ page_number: 1, text: text || undefined });
  }

  return {
    data: searchQuery.data,
    isLoading: searchQuery.isLoading || selectedQuery.isLoading,
    options,
    search,
  };
};

export const useJobRolesSearchInfinite = (filter: ApiJobRoleFilter, enabled: boolean = true) => {
  return useInfiniteQuery({
    ...jobRolesKeys.searchInfinite(filter),
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiJobRoles.search({ ...filter, page_number: pageParam }, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
    placeholderData: (prevData) => prevData,
  });
};

export const useJobRoleById = (id: number, enabled: boolean = true) => {
  return useQuery({
    ...jobRolesKeys.byId(id),
    staleTime: 60 * 1000,
    enabled: enabled && id > 0,
  });
};

export interface JobRoleSaveParams {
  id: number;
  payload: ApiJobRoleSavePayload;
}

export const useJobRoleSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: JobRoleSaveParams) => {
      const result =
        id == 0 ? await apiJobRoles.create(payload) : await apiJobRoles.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.searchInfinite._def });
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.byId._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useJobRoleDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiJobRoles.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.searchInfinite._def });
      void queryClient.invalidateQueries({
        queryKey: jobRolesKeys.byId._def,
        refetchType: "none",
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
